import { createSprigLogger } from './logger'
import type {
  SprigConfig,
  SprigIdentifyOptions,
  SprigTrackOptions,
} from './types'

// Configuration constants
const SPRIG_POLLING_INTERVAL_MS = 500
const SPRIG_CONFIGURATION_TIMEOUT_MS = 10000

let isConfigured = false
let configurationPromise: Promise<void> | null = null

export const configureSprig = async (config: SprigConfig): Promise<void> => {
  if (isConfigured) {
    return Promise.resolve()
  }

  // Return existing promise if configuration is in progress
  if (configurationPromise) {
    return configurationPromise
  }

  configurationPromise = (async () => {
    try {
      const { sprig } = await import('@sprig-technologies/sprig-browser')

      return new Promise<void>((resolve, reject) => {
        if (window.Sprig !== undefined) {
          isConfigured = true
          resolve()
          return
        }

        if (!config.environmentId) {
          reject(new Error('No Sprig environmentId provided'))
          return
        }

        sprig.configure({ environmentId: config.environmentId })

        const intervalId = setInterval(() => {
          if (window.Sprig !== undefined) {
            clearInterval(intervalId)
            clearTimeout(timeoutId)
            isConfigured = true
            resolve()
          }
        }, SPRIG_POLLING_INTERVAL_MS)

        // Timeout after configured duration
        const timeoutId = setTimeout(() => {
          clearInterval(intervalId)
          clearTimeout(timeoutId)
          if (!isConfigured) {
            reject(new Error('Sprig configuration timeout'))
          }
        }, SPRIG_CONFIGURATION_TIMEOUT_MS)
      })
    } catch (error) {
      configurationPromise = null // Reset so it can be retried
      throw new Error(`Failed to load Sprig: ${error}`)
    }
  })()

  return configurationPromise
}

const executeSprigSafely = async (
  config: SprigConfig,
  callback: () => void,
  logger?: { warn: (message: string, error?: unknown) => void },
): Promise<void> => {
  try {
    await configureSprig(config)
    callback()
  } catch (error) {
    if (logger) {
      logger.warn('Sprig operation failed', error)
    }
  }
}

export const createSprigActions = (config: SprigConfig) => {
  const logger = createSprigLogger(config.enableLogging, config.logPrefix)

  const identify = (options: SprigIdentifyOptions): void => {
    const { userId, anonymousId, attributes } = options

    if (userId) {
      executeSprigSafely(
        config,
        () => window.Sprig?.('setUserId', userId),
        logger,
      )
      logger.log('setUserId', { userId })
    } else if (anonymousId) {
      executeSprigSafely(
        config,
        () => window.Sprig?.('setUserId', anonymousId),
        logger,
      )
      logger.log('setUserId', { anonymousId })
    }

    if (attributes && Object.keys(attributes).length > 0) {
      executeSprigSafely(
        config,
        () => window.Sprig?.('setAttributes', attributes),
        logger,
      )
      logger.log('setAttributes', attributes)
    }
  }

  const track = (options: SprigTrackOptions): void => {
    const { eventName, properties, metadata, showSurveyCallback } = options

    executeSprigSafely(
      config,
      () =>
        window.Sprig?.(
          'track',
          eventName,
          properties,
          metadata,
          showSurveyCallback,
        ),
      logger,
    )
    logger.log('track', { eventName, properties, metadata })
  }

  const logout = (): void => {
    executeSprigSafely(config, () => window.Sprig?.('logoutUser'), logger)
    logger.log('logoutUser')
  }

  return {
    identify,
    track,
    logout,
  }
}
